





































































































import { Component, Vue } from "vue-property-decorator";
import { RoomServiceAPI } from "@/api/room-service/room-service-api";
import {
  IRoomServiceRequest,
  TRoomServicePriority,
  TRoomServiceType,
} from "@/@types/room-service/room-service";

import RequestCard from "@/components/room-service/RequestCard.vue";
import RequestDetailModal from "@/components/room-service/RequestDetailModal.vue";
import RequestCreateModal from "@/components/room-service/RequestCreateModal.vue";
import RoomPlanModal from "@/components/room-service/RoomPlanModal.vue";
import RoomRequestsModal from "@/components/room-service/RoomRequestsModal.vue";
import InputSearch from "@/components/app/search/input-search.vue";
import { isRoomServiceTypeHidden } from "@/services/featureConfig";

@Component({
  components: {
    RequestCard,
    RequestDetailModal,
    RequestCreateModal,
    RoomPlanModal,
    RoomRequestsModal,
    "input-search": InputSearch,
  },
})
class RoomServicePage extends Vue {
  requests: IRoomServiceRequest[] = [];
  loading = false;
  filter = "ACTIVE";
  search = "";
  serviceFilter = "";
  priorityFilter = "";
  activeRequest: IRoomServiceRequest | null = null;
  showCreate = false;
  showPlan = false;
  showRoomRequests = false;
  roomRequests: IRoomServiceRequest[] = [];
  roomRequestsName = "";

  pollInterval: ReturnType<typeof setInterval> | undefined;

  serviceTypes: TRoomServiceType[] = (
    [
      "CONCIERGE_CALL",
      "WAKE_UP",
      "HYGIENE_SUPPLIES",
      "CLEANING_REQUEST",
      "DO_NOT_DISTURB",
      "EMERGENCY_ASSISTANCE",
    ] as TRoomServiceType[]
  ).filter((t) => !isRoomServiceTypeHidden(t));
  priorities: TRoomServicePriority[] = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];

  get tabs(): Array<{ value: string; label: string; count: number }> {
    return [
      {
        value: "ACTIVE",
        label: "roomService.filters.active",
        count: this.countFor([
          "CREATED",
          "ASSIGNED",
          "IN_PROGRESS",
          "ESCALATED",
        ]),
      },
      {
        value: "CREATED",
        label: "roomService.filters.new",
        count: this.countFor(["CREATED"]),
      },
      {
        value: "IN_PROGRESS",
        label: "roomService.filters.inProgress",
        count: this.countFor(["ASSIGNED", "IN_PROGRESS"]),
      },
      {
        value: "EMERGENCY",
        label: "roomService.filters.emergency",
        count: this.countEmergency(),
      },
      {
        value: "COMPLETED",
        label: "roomService.filters.completed",
        count: this.countFor(["COMPLETED"]),
      },
      {
        value: "CANCELLED",
        label: "roomService.filters.cancelled",
        count: this.countFor(["CANCELLED"]),
      },
    ];
  }

  countFor(statuses: string[]): number {
    return this.requests.filter((r) => statuses.includes(r.status)).length;
  }
  countEmergency(): number {
    return this.requests.filter(
      (r) =>
        r.serviceType === "EMERGENCY_ASSISTANCE" ||
        r.priority === "CRITICAL" ||
        r.status === "ESCALATED"
    ).length;
  }

  get filteredRequests(): IRoomServiceRequest[] {
    let list = this.requests;
    if (this.filter === "ACTIVE") {
      list = list.filter((r) =>
        ["CREATED", "ASSIGNED", "IN_PROGRESS", "ESCALATED"].includes(r.status)
      );
    } else if (this.filter === "EMERGENCY") {
      list = list.filter(
        (r) =>
          r.serviceType === "EMERGENCY_ASSISTANCE" ||
          r.priority === "CRITICAL" ||
          r.status === "ESCALATED"
      );
    } else {
      list = list.filter((r) => r.status === this.filter);
    }
    if (this.serviceFilter) {
      list = list.filter((r) => r.serviceType === this.serviceFilter);
    }
    if (this.priorityFilter) {
      list = list.filter((r) => r.priority === this.priorityFilter);
    }
    if (this.search.trim()) {
      const q = this.search.trim().toLowerCase();
      list = list.filter(
        (r) =>
          r.room_number.toLowerCase().includes(q) ||
          r.guestName.toLowerCase().includes(q) ||
          (r.requestNumber || "").toLowerCase().includes(q) ||
          (r.details?.description || "").toLowerCase().includes(q)
      );
    }
    const priorityRank: Record<string, number> = {
      CRITICAL: 0,
      HIGH: 1,
      MEDIUM: 2,
      LOW: 3,
    };
    return [...list].sort((a, b) => {
      const pr =
        (priorityRank[a.priority] ?? 2) - (priorityRank[b.priority] ?? 2);
      if (pr !== 0) return pr;
      const ad = new Date(
        a.timestamps?.createdAt || a.createdAt || 0
      ).getTime();
      const bd = new Date(
        b.timestamps?.createdAt || b.createdAt || 0
      ).getTime();
      return bd - ad;
    });
  }

  openDetail(req: IRoomServiceRequest): void {
    this.activeRequest = req;
  }

  onPlanSelectRoom(payload: {
    roomName: string;
    requests: IRoomServiceRequest[];
  }): void {
    this.showPlan = false;
    if (payload.requests.length === 1) {
      this.activeRequest = payload.requests[0];
      return;
    }
    this.roomRequestsName = payload.roomName;
    this.roomRequests = payload.requests;
    this.showRoomRequests = true;
  }

  onRoomRequestOpen(req: IRoomServiceRequest): void {
    this.showRoomRequests = false;
    this.activeRequest = req;
  }

  onUpdated(updated: IRoomServiceRequest): void {
    const i = this.requests.findIndex((r) => r._id === updated._id);
    if (i >= 0) this.$set(this.requests, i, updated);
    this.activeRequest = updated;
  }

  onCreated(): void {
    this.fetch();
  }

  async fetch(): Promise<void> {
    this.loading = true;
    try {
      const res = await RoomServiceAPI.getAll({ limit: 200 });
      if (res.status === 200) {
        const visible = res.data.data.filter(
          (r) => !isRoomServiceTypeHidden(r.serviceType)
        );
        this.requests = visible;
        this.$store.dispatch("setRoomServiceNewCount", 0);
        this.$store.dispatch(
          "setRoomServiceKnownIds",
          visible.map((r) => r._id)
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.loading = false;
    }
  }

  mounted(): void {
    this.fetch();
    this.pollInterval = setInterval(() => this.fetch(), this.$second(10));
  }

  beforeDestroy(): void {
    clearInterval(this.pollInterval);
  }
}
export default RoomServicePage;
